import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { PasswordResetService } from '../../services/session/password-reset.service';
import { NGXLogger } from 'ngx-logger';
import { CustomLoggerService } from '../../services/custom-logger.service';

  const INCORRECT_CREDS: string = 'Incorrect credentials provided when submitting a password reset request.';      

@Component({
  selector: 'app-password-reset',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.css'
})
export class PasswordResetComponent implements OnInit {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly passwordResetService = inject(PasswordResetService);
  private readonly logger = inject(NGXLogger);
  private readonly customLogger = inject(CustomLoggerService);
  private readonly destroyRef = inject(DestroyRef);

  title: string = 'Walletron Portal';
  subtitle: string = 'Password Reset';
  sessionId!: string;
  jwt!: string;

  pswResetForm!: FormGroup;  

  isSuccessMessageShown: boolean = false;
  isErrorMessageShown: boolean = false;
  isSubmitButtonShown: boolean = true;
  isPasswordShown: boolean = false;
  successMessage: string = '';
  errorMessage: string = '';

  public constructor(
    private readonly fb: FormBuilder, 
  ) {}  

 ngOnInit() {
    this.pswResetForm = this.fb.group({
      userName: ['', [Validators.required]],
      newPassword: ['', [Validators.required]],
      confirmPassword: ['', [Validators.required]],
    });  
    // Get the parameters from the caller 
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.sessionId = params.get('si') || '';
        this.jwt = params.get('jwt') || '';
      });    
    // No jwt provided
    if (!this.jwt) {
      this.errorMessage = INCORRECT_CREDS;
      this.isErrorMessageShown = true;
      this.customLogger.logException('psw-reset.ngOnInit()', 'PR-0', INCORRECT_CREDS, 'PSWD-RESET');
    }  
  }

  onSubmit(){
    this.isSuccessMessageShown = false;
    this.isErrorMessageShown = false;
    this.successMessage = '';
    this.errorMessage = '';

    let userName = this.pswResetForm.value.userName;
    let newPassword = this.pswResetForm.value.newPassword;
    let confirmPassword = this.pswResetForm.value.confirmPassword;

    // Make some preliminary verification
    if (newPassword != confirmPassword) {
      this.errorMessage = "New and confirm passwords do not match";
      this.isErrorMessageShown = true;
      return;
    }

     this.passwordResetService.postPasswordReset(this.sessionId, userName, newPassword, this.jwt)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          switch (response.status) {
            case 'BAD_REQUEST':
              this.errorMessage = response.returnMessage || 'System error.  Please see system administrator.';
              this.isErrorMessageShown = true;
              break;
            case 'ERROR':
              this.errorMessage = response.returnMessage || 'Sorry, looks like some kind of a system error happened.  Please try again.';
              this.isErrorMessageShown = true;
              break;
            case 'FAILURE':
              if (response.additionalInfo?.has('prStatus')) {
                switch(response.additionalInfo.get('prStatus')) {
                  case 'EXPIRED':
                    this.errorMessage = response.returnMessage || 'This password reset request has expired';
                    this.isSubmitButtonShown = false;
                    break;
                  case 'EXCEEDED':
                    this.errorMessage = 'You have exceeded the number of allowed reset request submissions';
                    this.isSubmitButtonShown = false;
                    break;
                  case 'INVALID':
                    this.errorMessage = response.returnMessage || 'Invalid request';
                    break;
                  case 'FAILED':
                    this.errorMessage = response.returnMessage || 'Invalid request';
                    this.isSubmitButtonShown = false;
                    break;
                }
              }
              this.isErrorMessageShown = true;
              break;
            case 'SUCCESS': {
              this.successMessage = response.returnMessage || '';
              this.isSuccessMessageShown = true;
              this.isSubmitButtonShown = false;
              console.log('postPasswordReset', response);
              const redirectUrl = response.additionalInfo?.get('redirectUrl');
              console.log('postPasswordReset - redirectUrl', redirectUrl);
              if (redirectUrl) {
                globalThis.location.href = redirectUrl;
              }
              break;
            }
          }
        },
        error: (error) => {
          this.customLogger.logException('ev-page.onSubmitForEv()', 'PR-1', error.message, 'PSWD-RESET');
        },
      });
  }  

  togglePasswordVisibility(): void {
    this.isPasswordShown = !this.isPasswordShown;
  }


}
